import { z } from 'zod/v4';

/**
 * Sort columns the impersonation-picker endpoint accepts. Each key maps to a
 * SQL expression in `core/db/users/list-to-impersonate.ts` (`SORT_COLUMNS`).
 */
export const AdminUsersListToImpersonateSortColumns = [
  'firstName',
  'lastName',
  'company',
] as const;

export type AdminUsersListToImpersonateSortColumn =
  (typeof AdminUsersListToImpersonateSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

export const AdminUsersListToImpersonateQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  filter: z.string().max(512).optional(),
});

export type AdminUsersListToImpersonateQuery = z.infer<
  typeof AdminUsersListToImpersonateQuerySchema
>;

/**
 * Row shape for the impersonation-picker dropdown. Field reads in the FE
 * (`useSelect`, `impersonate.jsx`, `add-tag-to-user-modal/form.jsx`) that
 * must stay populated: id, companyId, companyName, firstName. Retraced staff
 * are excluded from this list entirely by the underlying SQL's staff-tenant
 * `NOT EXISTS` clause (`core/db/users/list-to-impersonate.ts`), so there is
 * no client-visible staff signal to expose here.
 *
 * `roleNames` are the display names of every IAM role the user holds in
 * `companyId` (the row's own company), most senior first, shown next to the
 * person in the picker; empty when they hold none there.
 */
export const AdminUserToImpersonateRowSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  companyName: z.string().nullable(),
  companyId: z.string().nullable(),
  roleNames: z.array(z.string()),
});

export type AdminUserToImpersonateRow = z.infer<
  typeof AdminUserToImpersonateRowSchema
>;

export const AdminUsersListToImpersonateResponseSchema = z.object({
  data: z.array(AdminUserToImpersonateRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AdminUsersListToImpersonateResponse = z.infer<
  typeof AdminUsersListToImpersonateResponseSchema
>;
